<template>
  <v-card class="promoCodeCard wd-p-4 wd-pt-3 wd-border wd-border-solid wd-border-gray40 wd-rounded wd-self-baseline" elevation="0">
    <div class="cardContent__top">
      <p class="cardTitle" :class="[isActive ? 'wd-text-orange50 wd-text-otempmd' : 'inactive wd-text-gray60 wd-text-omd']">
        {{ promoCodeInfo.title }}
      </p>

      <content-show-more
        v-if="isActive"
        v-model="shouldAllShow"
        :hide="!shouldMoreInfoButtonShow"
        class-name="mb-0 wd-mt-4 wd-text-otempsm wd-px-3 wd-py-0.5"
      >
        <div ref="cardDescriptionRef" class="cardDescription wd-text-omd" :class="shouldAllShow ? 'wd-line-clamp-none' : 'wd-line-clamp-2'">
          <!-- Must have div below to prevent content overlap -->
          <div>
            <template v-if="isInModal">
              <!-- condition in content -->
              <div v-html="nl2br(promoCodeInfo.content)" />
            </template>
            <template v-else>
              {{ promoCodeInfo.content }}
              <template v-if="promoCodeInfo.condition">
                <br />
                {{ promoCodeInfo.condition }}
              </template>
            </template>
          </div>
        </div>
        <div v-if="isHeightFixed && !shouldMoreInfoButtonShow" class="wd-h-10" />
      </content-show-more>
    </div>

    <hr class="wd-my-3 wd-border-0 wd-border-t wd-border-dashed" :class="isActive ? 'wd-border-orange50' : 'inactive wd-border-gray60'" />

    <div class="cardContent__bottom wd-flex wd-justify-between wd-items-start wd-min-h-42px">
      <div class="wd-flex wd-flex-col wd-justify-center wd-gap-0.5 wd-mr-2" :class="[isActive ? 'wd-text-gray80' : 'wd-text-gray60']">
        <template v-if="!isInModal">
          <p v-if="isActive && promoCodeInfo.usage_rate">{{ promoCodeInfo.usage_rate }}</p>
          <p>{{ $t('promo_expired_date') }} {{ expiredAt }}</p>
          <p v-if="promoCodeInfo.times > 1 && isActive">
            <span class="wd-mr-3">{{ $t('promo_total_times') }} {{ promoCodeInfo.times }}</span>
            <span>{{ $t('promo_code_remain_times') }} {{ promoCodeInfo.remain_times }}</span>
          </p>
        </template>
        <template v-else>
          <p v-if="promoCodeInfo.usage_rate">{{ promoCodeInfo.usage_rate }}</p>
          <p v-if="inputExpiredAtText">{{ inputExpiredAtText }}</p>
        </template>
      </div>

      <template v-if="isInModal">
        <v-btn
          :class="{ disabled: promoCodeInfo.status !== PromoCodeButtonStatus.ACTIVE }"
          height="40"
          :color="promoCodeInfo.status === PromoCodeButtonStatus.ACTIVE ? 'orange50' : 'orange30'"
          variant="flat"
          @click="claimPromoCode(promoCodeInfo.code)"
        >
          <Loading v-if="isButtonLoading" />
          <span v-else class="wd-text-white">{{ buttonText }}</span>
        </v-btn>
      </template>

      <template v-else>
        <v-btn v-if="isActive && promoCodeInfo.path" class="text-white" height="40" color="orange50" variant="flat" @click="goTo">
          {{ $t('all_discover') }}
        </v-btn>
      </template>

      <p v-if="!isActive" class="wd-text-gray60">
        {{ promoCodeStatus }}
      </p>
    </div>
  </v-card>
</template>

<script>
import { templateRef } from '@vueuse/core'
import { PromoCodeStatusEnum, PromoCodeButtonStatus } from '@/view-models/member'
import utils from '@/utils'
import { GtmSource } from '@/view-models/gtm'
import { pushGtmSource } from '@/composition/useGtmSource'

import ContentShowMore from '@/components/ContentShowMore.vue'
import Loading from '@/components/icons/loading.vue'

export default defineComponent({
  name: 'PromoCodeCard',
  components: {
    ContentShowMore,
    Loading,
  },
  props: {
    isActive: { type: Boolean, default: true },
    promoCodeInfo: { type: Object, default: () => ({}) },
    isHeightFixed: { type: Boolean, default: false },
    isInModal: { type: Boolean, default: false },
    isButtonLoading: { type: Boolean, default: false },
  },
  emits: ['buttonClick'],
  setup(props, { emit }) {
    const nuxtApp = useNuxtApp()
    const router = useRouter()
    const cardDescriptionRef = templateRef('cardDescriptionRef')
    const shouldMoreInfoButtonShow = ref(false)
    const shouldAllShow = ref(false)

    const expiredAt = computed(() => nuxtApp.$dayjs.utc(props.promoCodeInfo.end_at).local().format('YYYY/MM/DD HH:mm'))
    const inputExpiredAtText = computed(() =>
      props.promoCodeInfo.end_at
        ? `${nuxtApp.$i18n.t('promocode_claim_date')} ${nuxtApp.$dayjs.utc(props.promoCodeInfo.end_at).local().format('YYYY/MM/DD HH:mm')}`
        : ''
    )

    const buttonText = computed(() => {
      if (props.promoCodeInfo.status === PromoCodeButtonStatus.ACTIVE) {
        return nuxtApp.$i18n.t('all_claim')
      }
      return props.promoCodeInfo.status === PromoCodeButtonStatus.CLAIMED
        ? nuxtApp.$i18n.t('promocode_claimed')
        : nuxtApp.$i18n.t('promocode_all_claimed')
    })

    const promoCodeStatus = computed(() => {
      switch (props.promoCodeInfo.status) {
        case PromoCodeStatusEnum.USED:
          return nuxtApp.$i18n.t('promo_used_already')
        case PromoCodeStatusEnum.EXPIRED:
          return nuxtApp.$i18n.t('promo_expired_already')
        case PromoCodeStatusEnum.NOT_APPLICABLE:
          return nuxtApp.$i18n.t('promo_code_not_applicable')
        case PromoCodeStatusEnum.FULLY_USED:
          return nuxtApp.$i18n.t('promo_full_appled')
        case PromoCodeStatusEnum.ACTIVE:
        default:
          return ''
      }
    })

    onMounted(() => {
      if (!props.isActive) {
        return
      }
      shouldMoreInfoButtonShow.value = cardDescriptionRef?.value.scrollHeight > cardDescriptionRef?.value.clientHeight
    })

    function goTo() {
      pushGtmSource(GtmSource.PROMO_CODE)
      const path = props.promoCodeInfo.path
      if (path.startsWith('https')) {
        location.href = path
        return
      }
      const redirectUrl = path.startsWith('/') ? path : `/${path}`
      router.push(nuxtApp.$localePath(redirectUrl))
    }

    function claimPromoCode(code) {
      emit('buttonClick', code)
    }

    return {
      shouldMoreInfoButtonShow,
      shouldAllShow,
      nl2br: utils.nl2br,
      inputExpiredAtText,
      expiredAt,
      goTo,
      claimPromoCode,
      promoCodeStatus,
      PromoCodeButtonStatus,
      buttonText,
    }
  },
})
</script>

<style scoped lang="scss">
.promoCodeCard {
  width: 100%;

  .cardContent__top {
    .cardTitle {
      margin-bottom: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.inactive {
        margin-bottom: 0px;
      }
    }
    .cardDescription {
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;

      min-height: 48px;
    }
  }

  .cardContent__bottom {
    p {
      @apply wd-text-otempsm wd-m-0;
    }
  }

  .disabled {
    pointer-events: none;
  }
}
</style>
